import { useUserStore } from '../stores/userStore';
import type { GameArticle, GameArticleContentType, GameArticleFilters, SearchModel } from '../types';
import axiosInstance from './axiosInstance';

export const fetchGames = async (
  getToken: () => Promise<string>,
  filters?: GameArticleFilters,
  currency?: string,
  limit?: number,
  offset?: number,
  sort?: Array<{ field: string; direction: 'asc' | 'desc' }>,
) => {
  // Get user's currency preference if no currency is explicitly provided
  const userCurrency = useUserStore.getState().preferences.defaultCurrency || 'USD';
  const effectiveCurrency = currency || userCurrency;

  // Convert old filters to new search model format
  const conditions: any[] = [];

  if (filters?.search) {
    conditions.push({
      field: 'articleName',
      operator: 'contains',
      value: filters.search,
    });
  }

  if (filters?.type) {
    conditions.push({
      field: 'articleType',
      operator: 'equals',
      value: filters.type,
    });
  }

  if (filters?.status) {
    conditions.push({
      field: 'articleStatus',
      operator: 'equals',
      value: filters.status,
    });
  }

  if (filters?.packaging) {
    conditions.push({
      field: 'packaging',
      operator: 'equals',
      value: filters.packaging,
    });
  }

  if (filters?.releaseCountry) {
    conditions.push({
      field: 'releaseCountry',
      operator: 'equals',
      value: filters.releaseCountry,
    });
  }

  if (filters?.storage) {
    conditions.push({
      field: 'storage',
      operator: 'equals',
      value: filters.storage,
    });
  }

  if (filters?.tags?.length) {
    conditions.push({
      field: 'tags',
      operator: 'some',
      value: {
        tagName: {
          in: filters.tags,
        },
      },
    });
  }

  if (filters?.unlinked) {
    conditions.push({
      field: 'gameDbGameId',
      operator: 'is_null',
      value: null,
    });
  }

  if (filters?.systemId) {
    conditions.push({
      field: 'systems',
      operator: 'some',
      value: {
        systemId: {
          equals: Number.parseInt(filters.systemId, 10),
        },
      },
    });
  }

  if (filters?.variantId) {
    conditions.push({
      field: 'systems',
      operator: 'some',
      value: {
        variantId: {
          equals: Number.parseInt(filters.variantId, 10),
        },
      },
    });
  }

  if (filters?.gameplayStatus) {
    conditions.push({
      field: 'gameplayStatus',
      operator: 'equals',
      value: filters.gameplayStatus,
    });
  }

  // Build the search model
  const searchModel: SearchModel = {
    where:
      conditions.length === 1
        ? conditions[0]
        : conditions.length > 1
          ? {
              type: 'AND',
              conditions,
            }
          : undefined,
    sort,
    currency: effectiveCurrency,
    limit,
    offset,
  };

  const results = await searchGameArticles(getToken, searchModel);

  return results;
};

export const fetchGame = async (getToken: () => Promise<string>, id: string) => {
  // Get user's currency preference
  const userCurrency = useUserStore.getState().preferences.defaultCurrency || 'USD';

  const token = await getToken();
  const params = new URLSearchParams();
  params.append('currency', userCurrency);
  const response = await axiosInstance.get<GameArticle>(`/game-articles/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params,
  });

  return response.data;
};

export const createGame = async (
  getToken: () => Promise<string>,
  game: Omit<GameArticle, 'id' | 'userId' | 'dateAcquired'>,
) => {
  const token = await getToken();
  const response = await axiosInstance.post<GameArticle>('/game-articles', game, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

export const updateGame = async (getToken: () => Promise<string>, id: string, game: Partial<GameArticle>) => {
  const token = await getToken();
  const response = await axiosInstance.put<GameArticle>(`/game-articles/${id}`, game, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

export const deleteGame = async (getToken: () => Promise<string>, id: string) => {
  const token = await getToken();
  await axiosInstance.delete(`/game-articles/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const downloadCollectionExport = async (getToken: () => Promise<string>): Promise<string> => {
  const token = await getToken();
  let response;
  try {
    response = await axiosInstance.post('/game-articles/export', undefined, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    });
  } catch (error: any) {
    if (error.response?.data instanceof Blob) {
      try {
        error.response.data = JSON.parse(await error.response.data.text());
      } catch {
        // Preserve the original response when it is not a JSON API error.
      }
    }
    throw error;
  }

  const disposition = response.headers['content-disposition'] as string | undefined;
  const filename = disposition?.match(/filename="?([^";]+)"?/i)?.[1] || 'the-game-project-collection.xlsx';
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return filename;
};

export const fetchTags = async (getToken: () => Promise<string>) => {
  const token = await getToken();
  const response = await axiosInstance.get<string[]>('/game-articles/tags', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const fetchStorageLocations = async (getToken: () => Promise<string>) => {
  const token = await getToken();
  const response = await axiosInstance.get<string[]>('/game-articles/storage-locations', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const fetchSources = async (getToken: () => Promise<string>) => {
  const token = await getToken();
  const response = await axiosInstance.get<string[]>('/game-articles/sources', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const fetchGameArticleTypes = async (getToken: () => Promise<string>) => {
  const token = await getToken();
  const response = await axiosInstance.get<string[]>('/game-articles/types', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const fetchGameArticleContentTypes = async (getToken: () => Promise<string>) => {
  const token = await getToken();
  const response = await axiosInstance.get<GameArticleContentType[]>('/game-articles/content-types', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const fetchEarliestAcquiredDate = async (
  getToken: () => Promise<string>,
  gameDbGameId: string,
): Promise<string | null> => {
  const token = await getToken();
  const response = await axiosInstance.get<{ acquiredDate: string | null }>(
    `/game-articles/earliest-acquired/${gameDbGameId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data.acquiredDate;
};

export interface OtherCopy {
  id: string;
  articleName: string;
  packaging: string | null;
  version: string | null;
  releaseCountry: string | null;
  conditionScore: string | null;
  dateOrdered: string | null;
  dateAcquired: string | null;
  thumbnailUrl: string | null;
  systems: Array<{
    systemId: number;
    variantId: number | null;
    mediaFormat: string | null;
    isPrimary: boolean;
  }>;
}

export const fetchOtherCopies = async (getToken: () => Promise<string>, articleId: string): Promise<OtherCopy[]> => {
  const token = await getToken();
  const response = await axiosInstance.get<OtherCopy[]>(`/game-articles/${articleId}/other-copies`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Search game articles with complex filtering and sorting
 */
export const searchGameArticles = async (
  getToken: () => Promise<string>,
  searchModel: SearchModel,
): Promise<GameArticle[]> => {
  try {
    const token = await getToken();
    const response = await axiosInstance.put('/game-articles/search', searchModel, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error searching game articles:', error);
    throw error;
  }
};

/**
 * Get count of game articles matching search criteria
 */
export const searchGameArticlesCount = async (
  getToken: () => Promise<string>,
  searchModel: Omit<SearchModel, 'sort' | 'currency' | 'limit' | 'offset'>,
): Promise<{ count: number }> => {
  try {
    const token = await getToken();
    const response = await axiosInstance.put('/game-articles/search/count', searchModel, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error getting search count:', error);
    throw error;
  }
};
